import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {Modal} from '../../../shared/modal/Modal';
import {StackedSpacer} from '../../../shared/custom-features/spacer/StackedSpacer';
import {FormLabel} from '../../../shared/form/form-features/FormLabel';
import {FormInput} from '../../../shared/form/form-control/FormInput';
import {Row} from '../../../shared/grid/grid-features/Row.js';
import {Col} from '../../../shared/grid/grid-features/Col.js';
import {ErrorMessage, Field, FieldArray} from 'formik';
import AddArticleGroup from './AddArticleGroup.jsx';
import {Btn} from '../../../shared/elements/btn/Btn';
import {Icon} from '../../../shared/elements/icon/Icon';
import AddExtraWorkItem from './AddExtraWorkItem.jsx';
import {withTranslation} from 'react-i18next';
import {getInvoiceLogicTypes} from './endpoints';
import errorHandler from '../../../util/errorHandler';
import {ValidationErrorTxt} from '../../../shared/form/form-features/ValidationErrorTxt';

function AddEditCustomerModalBody({values, setFieldValue, errors, setFieldTouched, touched, t}) {
    const [invoiceLogicTypes, setInvoiceLogicTypes] = useState([]);

    useEffect(() => {
        getInvoiceLogicTypes()
            .then((res) => setInvoiceLogicTypes(res.data))
            .catch((res) => errorHandler(res.data));
    }, []);

    if (invoiceLogicTypes.length === 0) return <p>{t('general.Loading')}</p>;

    return (
        <Modal.Body>
            <StackedSpacer space="xl">
                <Wrapper>
                    <Row>
                        <Col colsize="50">
                            <FormLabel>{t('customer.Name')}</FormLabel>
                            <FormInput name="name" placeholder={t('customer.Name')} />
                        </Col>

                        <Col colsize="25">
                            <FormLabel>{t('customer.PlantName')}</FormLabel>
                            <FormInput name="plantName" placeholder={t('general.ForExampleAbbr') + ' 122N'} />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.InvoiceLogicType')}</FormLabel>
                            <RadioWrapper role="group" aria-labelledby="my-radio-group">
                                <div>
                                    {_.map(invoiceLogicTypes, (ilt, ix) => (
                                        <label key={ix}>
                                            <Field type="radio" name="invoiceLogicType" value={ilt} />
                                            {ilt}
                                        </label>
                                    ))}
                                </div>
                            </RadioWrapper>
                            <ErrorMessage name="invoiceLogicType">
                                {(msg) => <ValidationErrorTxt>{msg}</ValidationErrorTxt>}
                            </ErrorMessage>
                        </Col>
                    </Row>
                    <Row>
                        <Col colsize="25">
                            <FormLabel>{t('customer.PriceLaborSaturdaySupervisor')}</FormLabel>
                            <FormInput name="priceLaborSaturdaySupervisor" prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.PriceLaborSundayHolidaySupervisor')}</FormLabel>
                            <FormInput name="priceLaborSundayHolidaySupervisor" prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.PriceLaborSaturdayForklift')}</FormLabel>
                            <FormInput name="priceLaborSaturdayForklift" prefixIcon="euro" placeholder="0.00" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.PriceLaborSundayHolidayForklift')}</FormLabel>
                            <FormInput name="priceLaborSundayHolidayForklift" prefixIcon="euro" placeholder="0.00" />
                        </Col>
                    </Row>
                    <Row>
                        <Col colsize="25">
                            <FormLabel>{t('customer.SurchargeInHandlingSat')}</FormLabel>
                            <FormInput name="surchargeInHandlingSat" suffixIcon="percent" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.SurchargeInHandlingSun')}</FormLabel>
                            <FormInput name="surchargeInHandlingSun" suffixIcon="percent" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.SurchargeOutHandlingSat')}</FormLabel>
                            <FormInput name="surchargeOutHandlingSat" suffixIcon="percent" />
                        </Col>
                        <Col colsize="25">
                            <FormLabel>{t('customer.SurchargeOutHandlingSun')}</FormLabel>
                            <FormInput name="surchargeOutHandlingSun" suffixIcon="percent" />
                        </Col>
                    </Row>
                </Wrapper>

                <div>
                    <FormLabel>{t('customer.ArticleGroups')}</FormLabel>
                    <FieldArray
                        name="articleGroups"
                        render={(arrayHelpers) => (
                            <div>
                                {values.articleGroups.length > 0 &&
                                    values.articleGroups.map((ag, index) => (
                                        <div key={index}>
                                            <AddArticleGroup
                                                key={index}
                                                articleGroup={ag}
                                                path={`articleGroups[${index}]`}
                                                setFieldValue={setFieldValue}
                                                setFieldTouched={setFieldTouched}
                                                onDelete={() => arrayHelpers.remove(index)}
                                                showErrors={
                                                    !_.isEmpty(errors.articleGroups?.[index]) &&
                                                    Boolean(touched.articleGroups?.[index])
                                                }
                                            />
                                        </div>
                                    ))}
                            </div>
                        )}
                    />

                    <Btn
                        fullWidth
                        ghost
                        color="primary"
                        btnIcon
                        onClick={() =>
                            setFieldValue('articleGroups', [
                                ...values.articleGroups,
                                {
                                    name: '',
                                    startDate: '',
                                    endDate: '',
                                    fixedQuantity: '',
                                    variableQuantity: '',
                                    pricePerPalletFixed: '',
                                    pricePerPalletVariable: '',
                                    pricePerPalletHandlingIn: '',
                                    pricePerPalletHandlingOut: '',
                                    articleGroupType: '',
                                },
                            ])
                        }
                    >
                        <Icon icon="Add" />
                    </Btn>
                </div>
                <div>
                    <FormLabel>{t('customer.ExtraWorkItems')}</FormLabel>
                    <FieldArray
                        name="extraWorkItems"
                        render={(arrayHelpers) => (
                            <div>
                                {values.extraWorkItems.length > 0 &&
                                    values.extraWorkItems.map((ew, index) => (
                                        <div key={index}>
                                            <AddExtraWorkItem
                                                key={index}
                                                extraWorkItem={ew}
                                                path={`extraWorkItems[${index}]`}
                                                setFieldValue={setFieldValue}
                                                setFieldTouched={setFieldTouched}
                                                onDelete={() => arrayHelpers.remove(index)}
                                                // show the user there are errors in a collapsed field
                                                showErrors={
                                                    !_.isEmpty(errors.extraWorkItems?.[index]) &&
                                                    Boolean(touched.extraWorkItems?.[index])
                                                }
                                            />
                                        </div>
                                    ))}
                            </div>
                        )}
                    />

                    <Btn
                        ghost
                        type="button"
                        fullWidth
                        color="primary"
                        btnIcon
                        onClick={() =>
                            setFieldValue('extraWorkItems', [
                                ...values.extraWorkItems,
                                {
                                    description: '',
                                    startDate: '',
                                    endDate: '',
                                    pricePerUnit: '',
                                    unit: '',
                                },
                            ])
                        }
                    >
                        <Icon icon="Add" />
                    </Btn>
                </div>
            </StackedSpacer>
        </Modal.Body>
    );
}

export default withTranslation()(AddEditCustomerModalBody);

const Wrapper = styled.div`
    ${Row} {
        align-items: baseline;
    }
`;
const RadioWrapper = styled.div`
    height: 40px;
    > div {
        display: flex;
        align-items: center;
        height: 100%;
    }
`;
